import { useEffect, useState } from 'react';
import { DataTable } from '../category/DataTable';
import { getMultiNumericFeature, deleteMultiNumericFeature } from '../../store/states/FeatureSlice';
import {
  EActionType,
  ECommonDialogType,
  EDataTableType,
  MultinumericTabs,
} from '../../data/Constants';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { openDialog } from '../../store/states/DialogSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store/AppStore';
import type { AppDispatch } from '../../store/AppStore';
import { TabDrawer } from '../../components/TabDrawer';
import PinIcon from '@mui/icons-material/Pin';
import type {
  IDataTableDeleteProps,
  IMultiNumericFeatureData,
} from '../../../types/IComponentTypes';
import type { ISearchRequest } from '../../../types/IRequestTypes';
import type { GridRowSelectionModel, GridFilterModel } from '@mui/x-data-grid';

export const MultiNumericFeatureManagement: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [loading, setLoading] = useState(false);
  //selector
  const data = useSelector((state: AppState) => state.multiNumeric);

  //Call the fetch api to fetch the data to render the rows
  useEffect(() => {
    if (!data?.data[1]) {
      fetchMultiNumericData({ page: 1, limit: 15, searchCode: '' });
    }
  }, []);

  /**
   * This method call the get api to fetch the multi numeric response
   * @param {ISearchRequest} request
   */
  const fetchMultiNumericData = (request: ISearchRequest) => {
    setLoading(true);
    dispatch(getMultiNumericFeature(request)).finally(() => setLoading(false));
  };

  /**
   * This function call the delete api to delete the selected rows from the data table
   * @param {IDataTableDeleteProps} props
   */
  const handleClickDelete = async (props: IDataTableDeleteProps) => {
    const { page, limit, searchCode } = props;

    // Get the current page data
    const pageData = data?.data?.[page];

    //get the active rows from the selected rows list
    const activeRows = Array.from(selectionModel.ids).filter(
      (id: string) => pageData.find((row) => row.code === id)?.isActive
    );

    const response = await dispatch(deleteMultiNumericFeature({ request: activeRows }));

    if (response.payload && !response.payload.error) {
      //reset selection model to remove the check box selection
      setSelectionModel({ type: 'include', ids: new Set() });

      //call the delete api to delete the rows
      dispatch(getMultiNumericFeature({ page, limit, searchCode }));

      //show the success message after deleted records from the DB
      dispatch(
        openDialog({
          type: ECommonDialogType.Success,
          data: t('messages.submit.DeleteRequestSuccess', {
            length: activeRows.length,
            list: activeRows,
          }),
        })
      );
    } else {
      // Show error dialog if deletion fails
      dispatch(
        openDialog({ type: ECommonDialogType.Error, data: t('messages.submit.DeleteRequest') })
      );
    }
  };

  /**
   * This method triggers when click the new button to create the new item in the data table.
   */
  const handleClickNew = () => {
    dispatch(
      openDialog({
        type: ECommonDialogType.MultiNumericManipulation,
        title: t('labels.multiNumericAction', { Action: t('button.create') }),
        data: { action: EActionType.Create },
      })
    );
  };

  /**
   * This method triggers when click on the code from the data table rows
   * @param {IMultiNumericFeatureData} row
   * @returns
   */
  function handleClickEdit(code: IMultiNumericFeatureData): void {
    dispatch(
      openDialog({
        type: ECommonDialogType.MultiNumericManipulation,
        title: t('labels.multiNumericAction', { Action: t('tooltip.edit') }),
        data: { action: EActionType.Edit, code: code },
      })
    );
  }

  return (
    <Box className="h-inherit">
      <Box className="d-flex h-inherit">
        <TabDrawer
          tabsConfig={{
            pathBase: '/',
            tabsData: MultinumericTabs,
            icons: {
              multinumeric: <PinIcon style={{ marginRight: '10px' }} />,
            },
          }}
        />
        <Box className="middle-container">
          <h3 className="mainHeader"> {t('labels.multiNumericManagement')} </h3>
          <Box className="data-table-container" style={{ position: 'relative' }}>
            <DataTable
              className="data-table"
              tableType={EDataTableType.MultiNumeric}
              loading={loading}
              setLoading={setLoading}
              selectionModel={selectionModel}
              filterModel={filterModel}
              fetchData={fetchMultiNumericData}
              setSelectionModel={setSelectionModel}
              setFilterModel={setFilterModel}
              handleClickDelete={handleClickDelete}
              handleClickNew={handleClickNew}
              handleClickEdit={handleClickEdit}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
